import { isRulebookEnabled } from '../utils/rulebookUtils';
/**
 * Service for managing rulebook settings and filtering game content
 * Handles enabling/disabling rulebooks and validating character data
 * Supports setting-based rulebook management (Dark Heresy, Rogue Trader, etc.)
 */

import {
  Rulebook,
  RulebookSettings,
  FilteredGameData,
  RulebookValidationResult
} from '../types/rulebook';
import { gameDataService } from './GameDataService';
import type { Character } from '../types/character';
import rulebooksData from '../data/rulebooks.json';
import shopItemsData from '../data/items.json';

interface RulebookDefinition {
  id: string;
  name: string;
  shortName: string;
  type: string;
  year: number;
  publisher: string;
  description: string;
  enabled: boolean;
}

interface SettingDefinition {
  name: string;
  description: string;
  primaryColor: string;
  enabled: boolean;
  rulebooks: RulebookDefinition[];
  adventures?: RulebookDefinition[];
}

export class RulebookService {
  private static instance: RulebookService;
  private settings: RulebookSettings;
  private availableRulebooks: Rulebook[] = [];
  private rulebookDefinitions: Record<string, SettingDefinition> = {};

  private constructor() {
    this.loadRulebookDefinitions();
    this.settings = this.loadSettings();
    this.initializeRulebooks();
  }

  public static getInstance(): RulebookService {
    if (!RulebookService.instance) {
      RulebookService.instance = new RulebookService();
    }
    return RulebookService.instance;
  }

  /**
   * Load rulebook definitions from the comprehensive data file
   */
  private loadRulebookDefinitions(): void {
    this.rulebookDefinitions = rulebooksData.settings as Record<string, SettingDefinition>;
  }

  /**
   * Initialize available rulebooks from both game data and comprehensive definitions
   */
  private initializeRulebooks(): void {
    // Get rulebooks that have character creation data
    const characterDataRulebookIds = gameDataService.getAvailableRulebooks();

    // Get rulebooks that have shop data
    const shopDataRulebookIds = this.getRulebooksWithShopData();

    // Get all rulebooks from comprehensive definitions
    const allComprehensiveRulebooks = new Set<string>();
    Object.values(this.rulebookDefinitions).forEach((setting) => {
      setting.rulebooks.forEach((book) => allComprehensiveRulebooks.add(book.id));
      setting.adventures?.forEach((adventure) => allComprehensiveRulebooks.add(adventure.id));
    });

    // Combine all sets
    const allRulebookIds = new Set([
      ...allComprehensiveRulebooks,
      ...characterDataRulebookIds,
      ...shopDataRulebookIds
    ]);

    this.availableRulebooks = Array.from(allRulebookIds).map((id) => {
      const definition = this.findRulebookDefinition(id);
      const hasCharacterData = characterDataRulebookIds.includes(id);
      const hasShopData = shopDataRulebookIds.includes(id);
      const hasAnyData = hasCharacterData || hasShopData;

      return {
        id,
        name: definition?.name || this.getRulebookDisplayName(id),
        shortName: definition?.shortName || this.getRulebookShortName(id),
        version: '1.0.0',
        enabled: this.settings.enabledRulebooks.includes(id),
        description:
          definition?.description ||
          this.getRulebookDescription(id) + (hasAnyData ? '' : ' (No data available yet)'),
        setting: this.getSettingForRulebook(id),
        type: definition?.type,
        year: definition?.year,
        publisher: definition?.publisher
      };
    });
  }

  /**
   * Get rulebooks that have shop data by checking shop settings
   */
  private getRulebooksWithShopData(): string[] {
    try {
      const rulebookIds = new Set<string>();
      const items = Array.isArray(shopItemsData) ? shopItemsData : shopItemsData.items || [];

      items.forEach((item: { rulebook?: string | string[] }) => {
        if (item.rulebook) {
          if (Array.isArray(item.rulebook)) {
            item.rulebook.forEach((rb) => rulebookIds.add(rb));
          } else {
            rulebookIds.add(item.rulebook);
          }
        }
      });

      return Array.from(rulebookIds);
    } catch (error) {
      console.warn('Could not load shop items for rulebook detection:', error);
      return [];
    }
  }

  /**
   * Find a rulebook definition across all settings
   */
  private findRulebookDefinition(rulebookId: string): RulebookDefinition | null {
    for (const setting of Object.values(this.rulebookDefinitions)) {
      const rulebook = setting.rulebooks.find((r) => r.id === rulebookId);
      if (rulebook) return rulebook;

      const adventure = setting.adventures?.find((a) => a.id === rulebookId);
      if (adventure) return adventure;
    }
    return null;
  }

  /**
   * Get the setting ID for a rulebook
   */
  private getSettingForRulebook(rulebookId: string): string | undefined {
    for (const [settingId, setting] of Object.entries(this.rulebookDefinitions)) {
      const hasRulebook = setting.rulebooks.some((r) => r.id === rulebookId);
      const hasAdventure = setting.adventures?.some((a) => a.id === rulebookId);

      if (hasRulebook || hasAdventure) {
        return settingId;
      }
    }
    return undefined;
  }

  /**
   * Get display name for a rulebook ID
   */
  private getRulebookDisplayName(id: string): string {
    const displayNames: Record<string, string> = {
      core: 'Rogue Trader Core Rulebook',
      'into-the-storm': 'Into the Storm',
      'hostile-acquisitions': 'Hostile Acquisitions',
      'the-navis-primer': 'The Navis Primer',
      'faith-and-coin': 'Faith and Coin',
      'battlefleet-koronus': 'Battlefleet Koronus',
      'the-koronus-bestiary': 'The Koronus Bestiary',
      'lure-of-the-expanse': 'Lure of the Expanse',
      'edge-of-the-abyss': 'Edge of the Abyss'
    };

    return displayNames[id] || id.charAt(0).toUpperCase() + id.slice(1).replace(/-/g, ' ');
  }

  /**
   * Get short name for a rulebook ID
   */
  private getRulebookShortName(id: string): string {
    const shortNames: Record<string, string> = {
      core: 'Core',
      'into-the-storm': 'ItS',
      'hostile-acquisitions': 'HA',
      'the-navis-primer': 'TNP',
      'faith-and-coin': 'F&C',
      'battlefleet-koronus': 'BK',
      'the-koronus-bestiary': 'TKB',
      'lure-of-the-expanse': 'LotE',
      'edge-of-the-abyss': 'EotA'
    };

    return shortNames[id] || id.toUpperCase();
  }

  /**
   * Get description for a rulebook ID
   */
  private getRulebookDescription(id: string): string {
    const descriptions: Record<string, string> = {
      core: 'The core Rogue Trader rulebook containing basic careers, homeworlds, and equipment.',
      'into-the-storm':
        'Expanded character options including new careers and alternate career ranks.',
      'hostile-acquisitions': 'Additional equipment, weapons, and starship components.',
      'the-navis-primer': 'Navigator-focused content and psychic powers.',
      'faith-and-coin': 'Missionary and trade-focused content.',
      'battlefleet-koronus': 'Starship combat and naval warfare options.',
      'the-koronus-bestiary': 'Creatures and xenos encountered in the Koronus Expanse.',
      'lure-of-the-expanse': 'Adventure content and additional background options.',
      'edge-of-the-abyss': 'High-level play content and advanced options.'
    };

    return descriptions[id] || `Content from the ${this.getRulebookDisplayName(id)} supplement.`;
  }

  /**
   * Load rulebook settings from localStorage
   */
  private loadSettings(): RulebookSettings {
    try {
      const stored = localStorage.getItem('rogue-trader-rulebook-settings');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          enabledRulebooks: parsed.enabledRulebooks || ['core'],
          lastUpdated: new Date(parsed.lastUpdated || Date.now())
        };
      }
    } catch (error) {
      console.warn('Failed to load rulebook settings:', error);
    }

    // Default settings - enable all Rogue Trader rulebooks for new users
    const defaultRulebooks = [
      'rogue-trader-core',
      'into-the-storm',
      'edge-of-the-abyss',
      'battlefleet-koronus',
      'hostile-acquisitions',
      'koronus-bestiary',
      'stars-of-inequity',
      'navis-primer',
      'lure-of-the-expanse',
      'faith-and-coin',
      'errata',
      'drydock',
      'dark-frontier',
      'forsaken-bounty',
      'game-masters-kit',
      'secrets-of-the-expanse',
      'twilight-crusade',
      'tau-character-guide',
      'the-dark-kin',
      'traitors-nexus',
      'the-soul-reaver',
      'converted-dh-psyker',
      'warpstorm-1',
      'warpstorm-2',
      'warpstorm-3'
    ];

    return {
      enabledRulebooks: defaultRulebooks,
      lastUpdated: new Date()
    };
  }

  /**
   * Save rulebook settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(
        'rogue-trader-rulebook-settings',
        JSON.stringify({
          enabledRulebooks: this.settings.enabledRulebooks,
          lastUpdated: this.settings.lastUpdated.toISOString()
        })
      );
    } catch (error) {
      console.error('Failed to save rulebook settings:', error);
    }
  }

  /**
   * Get all available rulebooks
   */
  public getAvailableRulebooks(): Rulebook[] {
    return [...this.availableRulebooks];
  }

  /**
   * Get currently enabled rulebooks
   */
  public getEnabledRulebooks(): Rulebook[] {
    return this.availableRulebooks.filter((rulebook) => rulebook.enabled);
  }

  /**
   * Get list of enabled rulebook IDs
   */
  public getEnabledRulebookIds(): string[] {
    return this.settings.enabledRulebooks;
  }

  /**
   * Enable or disable a rulebook
   */
  public setRulebookEnabled(rulebookId: string, enabled: boolean): void {
    // All rulebooks can now be disabled - no restrictions
    const rulebook = this.availableRulebooks.find((r) => r.id === rulebookId);
    if (!rulebook) {
      console.warn(`Rulebook not found: ${rulebookId}`);
      return;
    }

    rulebook.enabled = enabled;

    if (enabled && !this.settings.enabledRulebooks.includes(rulebookId)) {
      this.settings.enabledRulebooks.push(rulebookId);
    } else if (!enabled) {
      this.settings.enabledRulebooks = this.settings.enabledRulebooks.filter(
        (id) => id !== rulebookId
      );
    }

    this.settings.lastUpdated = new Date();
    this.saveSettings();
  }

  /**
   * Enable multiple rulebooks at once
   */
  public setEnabledRulebooks(rulebookIds: string[]): void {
    // All rulebooks can now be disabled - no auto-inclusion of core
    const enabledIds = rulebookIds;

    this.settings.enabledRulebooks = enabledIds;
    this.settings.lastUpdated = new Date();

    // Update rulebook enabled flags
    this.availableRulebooks.forEach((rulebook) => {
      rulebook.enabled = enabledIds.includes(rulebook.id);
    });

    this.saveSettings();
  }

  /**
   * Get filtered game data based on enabled rulebooks
   */
  public getFilteredGameData(): FilteredGameData {
    const enabledIds = this.getEnabledRulebookIds();

    const allCareers = gameDataService.getCareers();
    const allHomeworlds = gameDataService.getHomeworlds();
    const allBirthrights = gameDataService.getBirthrights();
    const allLures = gameDataService.getLures();
    const allTrials = gameDataService.getTrials();
    const allMotivations = gameDataService.getMotivations();
    const allSkills = gameDataService.getSkills();
    const allTalents = gameDataService.getTalents();
    const allTraits = gameDataService.getTraits();
    const allWeapons = gameDataService.getWeapons();
    const allArmor = gameDataService.getArmor();
    const allEquipment = gameDataService.getEquipment();

    // Combine all equipment types
    const allEquipmentItems = [...allWeapons, ...allArmor, ...allEquipment];

    return {
      // Available content (from enabled rulebooks)
      careers: allCareers.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      homeworlds: allHomeworlds.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      birthrights: allBirthrights.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      lures: allLures.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      trials: allTrials.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      motivations: allMotivations.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      skills: allSkills.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      talents: allTalents.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      traits: allTraits.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),
      equipment: allEquipmentItems.filter((item) => isRulebookEnabled(item.rulebook, enabledIds)),

      // Unavailable content (from disabled rulebooks)
      unavailableItems: {
        careers: allCareers.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        homeworlds: allHomeworlds.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        birthrights: allBirthrights.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        lures: allLures.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        trials: allTrials.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        motivations: allMotivations.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        skills: allSkills.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        talents: allTalents.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        traits: allTraits.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds)),
        equipment: allEquipmentItems.filter((item) => !isRulebookEnabled(item.rulebook, enabledIds))
      }
    };
  }

  /**
   * Validate a character against currently enabled rulebooks
   */
  public validateCharacterRulebooks(character: Character): RulebookValidationResult {
    const enabledIds = this.getEnabledRulebookIds();
    const errors: string[] = [];
    const warnings: string[] = [];
    const missingRulebooks: string[] = [];

    // Check character's enabled rulebooks against available ones
    character.enabledRulebooks.forEach((rulebookId) => {
      if (!this.availableRulebooks.some((r) => r.id === rulebookId)) {
        missingRulebooks.push(rulebookId);
      }
    });

    // Check if character uses content from disabled rulebooks
    const checkRulebookUsage = (item: { rulebook: string }, itemType: string, itemName: string) => {
      if (!isRulebookEnabled(item.rulebook, enabledIds)) {
        warnings.push(`${itemType} "${itemName}" is from disabled rulebook: ${item.rulebook}`);
      }
    };

    // Check character creation choices
    if (character.career.id) {
      checkRulebookUsage(character.career, 'Career', character.career.name);
    }
    if (character.homeworld.id) {
      checkRulebookUsage(character.homeworld, 'Homeworld', character.homeworld.name);
    }
    if (character.birthright.id) {
      checkRulebookUsage(character.birthright, 'Birthright', character.birthright.name);
    }
    if (character.lure.id) {
      checkRulebookUsage(character.lure, 'Lure of the Void', character.lure.name);
    }
    if (character.trials.id) {
      checkRulebookUsage(character.trials, 'Trials and Travails', character.trials.name);
    }
    if (character.motivation.id) {
      checkRulebookUsage(character.motivation, 'Motivation', character.motivation.name);
    }

    // Check skills, talents, traits, equipment
    character.skills.forEach((skill) => checkRulebookUsage(skill, 'Skill', skill.name));
    character.talents.forEach((talent) => checkRulebookUsage(talent, 'Talent', talent.name));
    character.traits.forEach((trait) => checkRulebookUsage(trait, 'Trait', trait.name));
    character.equipment.forEach((equipment) =>
      checkRulebookUsage(
        equipment,
        'Equipment',
        gameDataService.getItemDisplayName(equipment.sourceId)
      )
    );
    character.weapons.forEach((weapon) =>
      checkRulebookUsage(weapon, 'Weapon', gameDataService.getItemDisplayName(weapon.sourceId))
    );
    character.armor.forEach((armor) =>
      checkRulebookUsage(armor, 'Armor', gameDataService.getItemDisplayName(armor.sourceId))
    );

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      missingRulebooks
    };
  }

  /**
   * Check if a specific item is available based on current rulebook settings
   */
  public isItemAvailable(item: { rulebook: string }): boolean {
    return this.getEnabledRulebookIds().includes(item.rulebook);
  }

  /**
   * Get unavailable items from a character
   */
  public getUnavailableCharacterContent(character: Character): {
    career?: boolean;
    homeworld?: boolean;
    birthright?: boolean;
    lure?: boolean;
    trials?: boolean;
    motivation?: boolean;
    skills: string[];
    talents: string[];
    traits: string[];
    equipment: string[];
    weapons: string[];
    armor: string[];
  } {
    const enabledIds = this.getEnabledRulebookIds();

    return {
      career: character.career.id && !enabledIds.includes(character.career.rulebook),
      homeworld: character.homeworld.id && !enabledIds.includes(character.homeworld.rulebook),
      birthright: character.birthright.id && !enabledIds.includes(character.birthright.rulebook),
      lure: character.lure.id && !enabledIds.includes(character.lure.rulebook),
      trials: character.trials.id && !enabledIds.includes(character.trials.rulebook),
      motivation: character.motivation.id && !enabledIds.includes(character.motivation.rulebook),
      skills: character.skills
        .filter((item) => !isRulebookEnabled(item.rulebook, enabledIds))
        .map((item) => item.id),
      talents: character.talents
        .filter((item) => !isRulebookEnabled(item.rulebook, enabledIds))
        .map((item) => item.id),
      traits: character.traits
        .filter((item) => !isRulebookEnabled(item.rulebook, enabledIds))
        .map((item) => item.id),
      equipment: character.equipment
        .filter((item) => !isRulebookEnabled(item.rulebook, enabledIds))
        .map((item) => item.id),
      weapons: character.weapons
        .filter((item) => !isRulebookEnabled(item.rulebook, enabledIds))
        .map((item) => item.id),
      armor: character.armor
        .filter((item) => !isRulebookEnabled(item.rulebook, enabledIds))
        .map((item) => item.id)
    };
  }

  /**
   * Reset to default settings (core rulebook only)
   */
  public resetToDefaults(): void {
    this.setEnabledRulebooks(['core']);
  }

  /**
   * Get all available settings with their rulebooks
   */
  public getAvailableSettings(): Record<
    string,
    SettingDefinition & { availableRulebooks: string[] }
  > {
    const result: Record<string, SettingDefinition & { availableRulebooks: string[] }> = {};
    const availableRulebookIds = gameDataService.getAvailableRulebooks();

    for (const [settingId, setting] of Object.entries(this.rulebookDefinitions)) {
      const availableRulebooks = [
        ...setting.rulebooks.filter((r) => availableRulebookIds.includes(r.id)).map((r) => r.id),
        ...(setting.adventures
          ?.filter((a) => availableRulebookIds.includes(a.id))
          .map((a) => a.id) || [])
      ];

      result[settingId] = {
        ...setting,
        availableRulebooks
      };
    }

    return result;
  }

  /**
   * Enable or disable an entire setting (all its rulebooks)
   */
  public setSettingEnabled(settingId: string, enabled: boolean): void {
    const setting = this.rulebookDefinitions[settingId];
    if (!setting) {
      console.warn(`Setting not found: ${settingId}`);
      return;
    }

    const availableRulebookIds = gameDataService.getAvailableRulebooks();
    const settingRulebookIds = [
      ...setting.rulebooks.map((r) => r.id),
      ...(setting.adventures?.map((a) => a.id) || [])
    ].filter((id) => availableRulebookIds.includes(id));

    let currentEnabled = [...this.settings.enabledRulebooks];

    if (enabled) {
      // Add all setting rulebooks that aren't already enabled
      settingRulebookIds.forEach((id) => {
        if (!currentEnabled.includes(id)) {
          currentEnabled.push(id);
        }
      });
    } else {
      // Remove all setting rulebooks, but never disable core
      currentEnabled = currentEnabled.filter(
        (id) => !settingRulebookIds.includes(id) || id === 'core'
      );
    }

    this.setEnabledRulebooks(currentEnabled);
  }

  /**
   * Check if a setting is enabled (all its available rulebooks are enabled)
   */
  public isSettingEnabled(settingId: string): boolean {
    const setting = this.rulebookDefinitions[settingId];
    if (!setting) return false;

    const availableRulebookIds = gameDataService.getAvailableRulebooks();
    const settingRulebookIds = [
      ...setting.rulebooks.map((r) => r.id),
      ...(setting.adventures?.map((a) => a.id) || [])
    ].filter((id) => availableRulebookIds.includes(id));

    return (
      settingRulebookIds.length > 0 &&
      settingRulebookIds.every((id) => this.settings.enabledRulebooks.includes(id))
    );
  }

  /**
   * Check if a setting is partially enabled (some but not all rulebooks enabled)
   */
  public isSettingPartiallyEnabled(settingId: string): boolean {
    const setting = this.rulebookDefinitions[settingId];
    if (!setting) return false;

    const availableRulebookIds = gameDataService.getAvailableRulebooks();
    const settingRulebookIds = [
      ...setting.rulebooks.map((r) => r.id),
      ...(setting.adventures?.map((a) => a.id) || [])
    ].filter((id) => availableRulebookIds.includes(id));

    const enabledCount = settingRulebookIds.filter((id) =>
      this.settings.enabledRulebooks.includes(id)
    ).length;

    return enabledCount > 0 && enabledCount < settingRulebookIds.length;
  }

  /**
   * Get rulebooks grouped by setting
   */
  public getRulebooksBySettings(): Record<string, Rulebook[]> {
    const result: Record<string, Rulebook[]> = {};

    for (const [settingId, setting] of Object.entries(this.rulebookDefinitions)) {
      const settingRulebooks: Rulebook[] = [];

      // Add main rulebooks
      setting.rulebooks.forEach((rulebookDef) => {
        const rulebook = this.availableRulebooks.find((r) => r.id === rulebookDef.id);
        if (rulebook) {
          settingRulebooks.push(rulebook);
        }
      });

      // Add adventures
      setting.adventures?.forEach((adventureDef) => {
        const adventure = this.availableRulebooks.find((r) => r.id === adventureDef.id);
        if (adventure) {
          settingRulebooks.push(adventure);
        }
      });

      if (settingRulebooks.length > 0) {
        result[settingId] = settingRulebooks;
      }
    }

    return result;
  }

  /**
   * Get fallback options when no content is available
   * This ensures there's always at least some content available for character creation
   */
  public getFallbackOptions(): {
    hasMinimumContent: boolean;
    missingContentTypes: string[];
    suggestedRulebooks: string[];
  } {
    const filteredData = this.getFilteredGameData();
    const missingContentTypes: string[] = [];
    const suggestedRulebooks: string[] = [];

    // Check for minimum required content
    if (filteredData.careers.length === 0) {
      missingContentTypes.push('careers');
    }
    if (filteredData.homeworlds.length === 0) {
      missingContentTypes.push('homeworlds');
    }
    if (filteredData.birthrights.length === 0) {
      missingContentTypes.push('birthrights');
    }
    if (filteredData.lures.length === 0) {
      missingContentTypes.push('lures');
    }
    if (filteredData.trials.length === 0) {
      missingContentTypes.push('trials');
    }
    if (filteredData.motivations.length === 0) {
      missingContentTypes.push('motivations');
    }

    // Suggest enabling core if it's somehow disabled
    if (!this.settings.enabledRulebooks.includes('core')) {
      suggestedRulebooks.push('core');
    }

    // If we have missing content, suggest the first available rulebook that has that content
    if (missingContentTypes.length > 0) {
      const allRulebooks = gameDataService.getAvailableRulebooks();
      const disabledRulebooks = allRulebooks.filter(
        (id) => !this.settings.enabledRulebooks.includes(id)
      );

      if (disabledRulebooks.length > 0) {
        suggestedRulebooks.push(disabledRulebooks[0]);
      }
    }

    return {
      hasMinimumContent: missingContentTypes.length === 0,
      missingContentTypes,
      suggestedRulebooks: [...new Set(suggestedRulebooks)] // Remove duplicates
    };
  }

  /**
   * Auto-enable minimum required rulebooks if no content is available
   */
  public ensureMinimumContent(): boolean {
    const fallback = this.getFallbackOptions();

    if (!fallback.hasMinimumContent && fallback.suggestedRulebooks.length > 0) {
      console.warn(
        'No minimum content available, auto-enabling suggested rulebooks:',
        fallback.suggestedRulebooks
      );

      const currentEnabled = [...this.settings.enabledRulebooks];
      fallback.suggestedRulebooks.forEach((rulebookId) => {
        if (!currentEnabled.includes(rulebookId)) {
          currentEnabled.push(rulebookId);
        }
      });

      this.setEnabledRulebooks(currentEnabled);
      return true;
    }

    return false;
  }
}

// Export singleton instance
export const rulebookService = RulebookService.getInstance();
